/**
 * FulfillmentList Page
 * 
 * Displays fulfillments grouped by user with expandable rows.
 * Each user row can be expanded to show all fulfillments for that user.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pagination } from '@/components/common/Table';
import { FulfillmentStatusBadge, RewardCategoryBadge } from '@/components/common/Badge';
import { useFulfillmentData } from '@/app/hooks/useFulfillmentData';
import { useRewardMetadata } from '@/app/hooks/useRewardMetadata';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import type { FulfillmentResponse, FulfillmentStatus } from '@/app/types/fulfillment';
import { FULFILLMENT_STATUS_OPTIONS } from '@/app/types/fulfillment';
import { auditService } from '@/app/services';
import { logger } from '@/app/utils/logger';
import './FulfillmentList.scss';

// Sort options for user groups
type GroupSortKey = 'user_id' | 'count' | 'latest' | 'pending';
const GROUP_SORT_OPTIONS: { value: GroupSortKey; label: string }[] = [
  { value: 'latest', label: 'Most Recent' },
  { value: 'user_id', label: 'User ID' },
  { value: 'count', label: 'Most Fulfillments' },
  { value: 'pending', label: 'Most Pending' },
];

// Grouped user data structure
interface UserFulfillmentGroup {
  user_id: string;
  fulfillments: FulfillmentResponse[];
  totalFulfillments: number;
  statusSummary: {
    pending: number;
    completed: number;
    failed: number;
  };
  latestInitiated: string;
}

export const FulfillmentList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActions } = useHeaderActions();
  const {
    fulfillments,
    totalCount,
    statusCounts,
    currentPage,
    pageSize,
    totalPages,
    filters,
    isLoading,
    error,
    applyFilters,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    pageSizeOptions,
  } = useFulfillmentData();

  // Fetch categories from backend metadata
  const { categories, loading: categoriesLoading } = useRewardMetadata();
  const categoryOptions = useMemo(() => {
    return [
      { value: '', label: 'All Categories' },
      ...categories.map(cat => ({
        value: cat,
        label: cat
          .split(/[\s-]/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '),
      })),
    ];
  }, [categories]);

  // Set header actions
  useEffect(() => {
    setActions(
      <button
        className="btn-secondary"
        onClick={refresh}
        disabled={isLoading}
      >
        ↻ Refresh
      </button>
    );
    return () => setActions(null);
  }, [setActions, refresh, isLoading]);

  // Route change detection - refetch when returning to list page
  const prevPathnameRef = React.useRef<string>('');
  useEffect(() => {
    const isReturningToList = location.pathname === '/fulfillments' && 
                              prevPathnameRef.current !== '/fulfillments' &&
                              prevPathnameRef.current !== '';
    
    if (isReturningToList && !isLoading) {
      refresh();
    }
    
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, refresh, isLoading]);

  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [groupSort, setGroupSort] = useState<GroupSortKey>('latest');
  
  // Local filter state for controlled inputs
  const [userIdFilter, setUserIdFilter] = useState(filters.user_id || '');
  const [statusFilter, setStatusFilter] = useState<FulfillmentStatus | ''>(filters.status || '');
  const [categoryFilter, setCategoryFilter] = useState(filters.category || '');

  // Apply status filter from navigation state (e.g., from Dashboard)
  useEffect(() => {
    const navState = location.state as { status?: FulfillmentStatus } | null;
    if (navState?.status && navState.status !== statusFilter) {
      setStatusFilter(navState.status);
      setFiltersCollapsed(false); // Show filter panel so user knows filter is applied
      applyFilters({ status: navState.status });
      // Clear the navigation state so filter doesn't re-apply on subsequent visits
      window.history.replaceState({}, document.title);
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if any filters are active
  const hasActiveFilters = Boolean(userIdFilter || statusFilter || categoryFilter);

  // Group fulfillments by user
  const groupedByUser = useMemo(() => {
    const groups = new Map<string, UserFulfillmentGroup>();
    
    fulfillments.forEach(fulfillment => {
      const existing = groups.get(fulfillment.user_id);
      if (existing) {
        existing.fulfillments.push(fulfillment);
        existing.totalFulfillments++;
        if (fulfillment.status === 'PENDING') existing.statusSummary.pending++;
        if (fulfillment.status === 'COMPLETED') existing.statusSummary.completed++;
        if (fulfillment.status === 'FAILED') existing.statusSummary.failed++;
        if (fulfillment.initiated_at > existing.latestInitiated) {
          existing.latestInitiated = fulfillment.initiated_at;
        }
      } else {
        groups.set(fulfillment.user_id, {
          user_id: fulfillment.user_id,
          fulfillments: [fulfillment],
          totalFulfillments: 1,
          statusSummary: {
            pending: fulfillment.status === 'PENDING' ? 1 : 0,
            completed: fulfillment.status === 'COMPLETED' ? 1 : 0,
            failed: fulfillment.status === 'FAILED' ? 1 : 0,
          },
          latestInitiated: fulfillment.initiated_at,
        });
      }
    });

    // Sort fulfillments within each group by date (newest first)
    groups.forEach(group => {
      group.fulfillments.sort((a, b) => 
        new Date(b.initiated_at).getTime() - new Date(a.initiated_at).getTime()
      );
    });

    // Sort groups based on selected sort option
    const groupArray = Array.from(groups.values());
    switch (groupSort) {
      case 'user_id':
        return groupArray.sort((a, b) => a.user_id.localeCompare(b.user_id));
      case 'count':
        return groupArray.sort((a, b) => b.totalFulfillments - a.totalFulfillments);
      case 'pending':
        return groupArray.sort((a, b) => b.statusSummary.pending - a.statusSummary.pending);
      case 'latest':
      default:
        return groupArray.sort((a, b) => 
          new Date(b.latestInitiated).getTime() - new Date(a.latestInitiated).getTime()
        );
    }
  }, [fulfillments, groupSort]);

  const toggleExpand = useCallback((userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedUsers(new Set(groupedByUser.map(g => g.user_id)));
  }, [groupedByUser]);

  const collapseAll = useCallback(() => {
    setExpandedUsers(new Set());
  }, []);

  const handleFulfillmentClick = useCallback((fulfillment: FulfillmentResponse) => {
    navigate(`/fulfillments/${fulfillment.fulfillment_id}`);
  }, [navigate]);

  const handleApplyFilters = useCallback(() => {
    applyFilters({
      user_id: userIdFilter || undefined,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
    });
  }, [applyFilters, userIdFilter, statusFilter, categoryFilter]);

  // Log search audit after results return with accurate count
  const prevLoadingRef = React.useRef(isLoading);
  useEffect(() => {
    // Detect transition from loading to not loading (data just loaded)
    if (prevLoadingRef.current && !isLoading) {
      const filtersToLog: Record<string, unknown> = {};
      if (filters.user_id) filtersToLog.user_id = filters.user_id;
      if (filters.status) filtersToLog.status = filters.status;
      if (filters.category) filtersToLog.category = filters.category;
      
      auditService.logRecordSearch('fulfillment', filtersToLog, totalCount, '/fulfillments')
        .catch((err) => logger.warn('Failed to log record search', { error: err }));
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, totalCount, filters]);

  const handleResetFilters = useCallback(() => {
    setUserIdFilter('');
    setStatusFilter('');
    setCategoryFilter('');
    resetFilters();
  }, [resetFilters]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  }, [handleApplyFilters]);

  // Handle quick filter from status card click
  const handleStatusCardClick = useCallback((status: FulfillmentStatus) => {
    // Toggle filter - if already filtered by this status, clear it
    if (statusFilter === status) {
      setStatusFilter('');
      setFiltersCollapsed(true);
      applyFilters({ status: undefined });
    } else {
      setStatusFilter(status);
      setFiltersCollapsed(false);
      applyFilters({ status });
    }
  }, [statusFilter, applyFilters]);

  // Status summary at top
  const renderStatusSummary = () => {
    if (!statusCounts) return null;
    
    // Calculate non-actionable count for display
    const nonActionableCount = statusCounts.FAILED - statusCounts.FAILED_ACTIONABLE;
    
    return (
      <div className="status-summary">
        <div 
          className={`status-item pending clickable ${statusFilter === 'PENDING' ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('PENDING')}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && handleStatusCardClick('PENDING')}
        >
          <span className="count">{statusCounts.PENDING}</span>
          <span className="label">Pending</span>
        </div>
        <div 
          className={`status-item completed clickable ${statusFilter === 'COMPLETED' ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('COMPLETED')}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && handleStatusCardClick('COMPLETED')}
        >
          <span className="count">{statusCounts.COMPLETED}</span>
          <span className="label">Completed</span>
        </div>
        <div 
          className={`status-item failed clickable ${statusFilter === 'FAILED' ? 'active' : ''}`}
          onClick={() => handleStatusCardClick('FAILED')}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && handleStatusCardClick('FAILED')}
        >
          <span className="count">{statusCounts.FAILED_ACTIONABLE}</span>
          <span className="label">Failed</span>
          {nonActionableCount > 0 && (
            <span className="sublabel" title="Migration and demo failures that don't require action">
              +{nonActionableCount} non-actionable
            </span>
          )}
        </div>
      </div>
    );
  };

  if (isLoading && fulfillments.length === 0) {
    return (
      <div className="fulfillment-list-page">
        <div className="loading">Loading fulfillments...</div>
      </div>
    );
  }

  return (
    <div className="fulfillment-list-page">
      {renderStatusSummary()}

      {/* Filters with collapsible toggle */}
      <div className="filters-section" onKeyPress={handleKeyPress}>
        <button 
          className="filters-toggle"
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          aria-expanded={!filtersCollapsed}
        >
          <span className="toggle-text">
            🔍 Filters
            {hasActiveFilters && <span className="active-indicator">●</span>}
          </span>
          <span className={`toggle-chevron ${filtersCollapsed ? '' : 'expanded'}`}>▼</span>
        </button>

        <div className={`filters-content ${filtersCollapsed ? 'collapsed' : ''}`}>
          <div className="filter-row">
            <div className="filter-group">
              <label>User ID</label>
              <input
                type="text"
                placeholder="Search user ID..."
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as FulfillmentStatus | '')}
              >
                <option value="">All Statuses</option>
                {FULFILLMENT_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                disabled={categoriesLoading}
              >
                {categoriesLoading ? (
                  <option value="">Loading categories...</option>
                ) : (
                  categoryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))
                )}
              </select>
            </div>
            <div className="filter-actions">
              <button className="btn-primary" onClick={handleApplyFilters}>
                Search
              </button>
              <button className="btn-secondary" onClick={handleResetFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          Error loading fulfillments: {error}
        </div>
      )}

      <div className="results-header">
        <div className="results-summary">
          {groupedByUser.length} users • {totalCount} total fulfillments
        </div>
        <div className="results-controls">
          <div className="sort-control">
            <label htmlFor="group-sort">Sort by:</label>
            <select
              id="group-sort"
              value={groupSort}
              onChange={(e) => setGroupSort(e.target.value as GroupSortKey)}
            >
              {GROUP_SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="expand-controls">
            <button onClick={expandAll} className="btn-text">
              Expand All
            </button>
            <button onClick={collapseAll} className="btn-text">
              Collapse All
            </button>
          </div>
        </div>
      </div>

      <div className="grouped-fulfillments">
        {groupedByUser.length === 0 ? (
          <div className="empty-state">No fulfillments found matching your filters.</div>
        ) : (
          groupedByUser.map(group => {
            const isExpanded = expandedUsers.has(group.user_id);
            return (
              <div key={group.user_id} className={`user-group ${isExpanded ? 'expanded' : ''}`}>
                {/* User Header Row */}
                <div 
                  className="user-header"
                  onClick={() => toggleExpand(group.user_id)}
                >
                  <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                  <a
                    href={`#/users/${group.user_id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/users/${group.user_id}`);
                    }}
                    className="user-id-link"
                  >
                    {group.user_id}
                  </a>
                  <span className="fulfillment-count">
                    {group.totalFulfillments} fulfillment{group.totalFulfillments !== 1 ? 's' : ''}
                  </span>
                  <span className="status-breakdown">
                    {group.statusSummary.pending > 0 && (
                      <span className="mini-badge pending">{group.statusSummary.pending} pending</span>
                    )}
                    {group.statusSummary.completed > 0 && (
                      <span className="mini-badge completed">{group.statusSummary.completed} completed</span>
                    )}
                    {group.statusSummary.failed > 0 && (
                      <span className="mini-badge failed">{group.statusSummary.failed} failed</span>
                    )}
                  </span>
                  <span className="latest-date">
                    {new Date(group.latestInitiated).toLocaleDateString()}
                  </span>
                </div>

                {/* Expanded Fulfillments */}
                {isExpanded && (
                  <div className="fulfillments-detail">
                    {/* Desktop Table View */}
                    <table className="fulfillments-table">
                      <thead>
                        <tr>
                          <th>Reward</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Initiated</th>
                          <th>Tracking ID</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.fulfillments.map(fulfillment => (
                          <tr 
                            key={fulfillment.fulfillment_id}
                            onClick={() => handleFulfillmentClick(fulfillment)}
                            className="fulfillment-row"
                          >
                            <td className="reward-cell">{fulfillment.reward_name || 'Unknown'}</td>
                            <td className="category-cell">
                              {fulfillment.reward_category && (
                                <RewardCategoryBadge category={fulfillment.reward_category} />
                              )}
                            </td>
                            <td className="status-cell">
                              <FulfillmentStatusBadge 
                                status={fulfillment.status} 
                                isActionable={fulfillment.is_actionable}
                              />
                            </td>
                            <td className="date-cell">
                              {new Date(fulfillment.initiated_at).toLocaleString()}
                            </td>
                            <td className="tracking-cell">
                              {fulfillment.external_tracking_id || '—'}
                            </td>
                            <td className="action-cell">
                              <button className="btn-view">View</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Mobile List View */}
                    <div className="fulfillments-cards">
                      {group.fulfillments.map(fulfillment => (
                        <div
                          key={fulfillment.fulfillment_id}
                          className="fulfillment-card"
                          onClick={() => handleFulfillmentClick(fulfillment)}
                        >
                          <div className="card-main">
                            <div className="card-title">{fulfillment.reward_name || 'Unknown'}</div>
                            <div className="card-meta">
                              {fulfillment.reward_category && (
                                <RewardCategoryBadge category={fulfillment.reward_category} />
                              )}
                              <span>{new Date(fulfillment.initiated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="card-badges">
                            <FulfillmentStatusBadge 
                              status={fulfillment.status}
                              isActionable={fulfillment.is_actionable}
                            />
                          </div>
                          <span className="card-arrow">›</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalCount}
        itemsPerPage={pageSize}
        onPageChange={goToPage}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={changePageSize}
        displayText={`Showing ${groupedByUser.length} user${groupedByUser.length !== 1 ? 's' : ''} (${totalCount} total fulfillment${totalCount !== 1 ? 's' : ''})`}
      />
    </div>
  );
};

export default FulfillmentList;
